import {
  Amenity,
  AvailabilityType,
  DealType,
  IGeneralCondition,
  ListingPoiType,
  ResolutionPreference,
} from 'utils/entities';
import { PropertyType } from 'components/filters/types';
import { AmenityCountType, SizedAmenityType } from './components/amenity-with-counter/types';
import { IAmenityWithExtraDataItem, AmenityWithExtraDataType } from './types';

export const COMMON_MAX_VALUE = 999999;
export const COMMON_CHARGES_MAX_VALUE = 100000;
export const NUMBER_OF_EMPLOYEES_MAX_VALUE = 10000;
export const MONTHLY_TAXES_MAX_VALUE = 999999;
export const MONTHLY_MANAGEMENT_FEE_MIN_VALUE = 5;
export const MONTHLY_MANAGEMENT_FEE_MAX_VALUE = 99999;
export const AREA_MAX_VALUE = 999999;
export const ROOMS_MAX_VALUE = 15;

export const TWO_MB_IN_BYTES = 2097152;
export const TWENTY_MB_IN_BYTES = 20971520;
export const HUNDRED_MB_IN_BYTES = 100 * 1024 ** 2;
export const EXCLUSIVITY_MAX_FILES_COUNT = 1;
export const MAX_UPLOAD_IMAGES = 20;

export const RESIDENTIAL_BUILDING_CLASSES = [
  PropertyType.Flat,
  PropertyType.PrivateHouse,
  PropertyType.GardenApartment,
  PropertyType.Cottage,
  PropertyType.TwoFamilyDwelling,
  PropertyType.Duplex,
  PropertyType.Land,
  PropertyType.PenthouseApp,
  PropertyType.RoofFlat,
  PropertyType.MiniPenthouse,
  PropertyType.ResidentialUnit,
  PropertyType.SerialCottage,
  PropertyType.Studio,
  PropertyType.Building,
  PropertyType.AgricultureFarm,
  PropertyType.Triplex,
  PropertyType.Basement,
  PropertyType.Storeroom,
  PropertyType.SplitFlat,
  PropertyType.AgricultureLand,
  PropertyType.VacationProperty,
  PropertyType.BuyerTeam,
  PropertyType.ProfessionalFarm,
  PropertyType.Parking,
  PropertyType.Other,
];

export const COMMERCIAL_BUILDING_CLASSES = [
  PropertyType.Office,
  PropertyType.Shop,
  PropertyType.SharedWorkSpace,
  PropertyType.Studio,
  PropertyType.Hall,
  PropertyType.Clinic,
  PropertyType.Restaurant,
  PropertyType.Hotel,
  PropertyType.Building,
  PropertyType.IndustrialBuilding,
  PropertyType.Storeroom,
  PropertyType.Basement,
  PropertyType.ParkingLot,
  PropertyType.Land,
  PropertyType.IncomeProducingProperty,
  PropertyType.AgricultureLand,
  PropertyType.FarmLand,
  PropertyType.Lab,
  PropertyType.Other,
];

export const RESIDENTIAL_GENERAL_CONDITION_OPTIONS = [
  IGeneralCondition.New,
  IGeneralCondition.AsNew,
  IGeneralCondition.Renovated,
  IGeneralCondition.Preserved,
  IGeneralCondition.ToRenovated,
];

export const COMMERCIAL_GENERAL_CONDITION_OPTIONS = [
  IGeneralCondition.Skin,
  IGeneralCondition.Finish,
  IGeneralCondition.FurnishedFinish,
  IGeneralCondition.FullFinish,
  IGeneralCondition.RequiresRenovation,
];

export const SECURE_ROOM_AMENITIES = [
  Amenity.SecureRoom,
  Amenity.Mamak,
  Amenity.Miklat,
];

export const RESIDENTIAL_AMENITIES = [
  Amenity.Elevator,
  Amenity.SecureRoom,
  Amenity.Mamak,
  Amenity.Miklat,
  Amenity.AirConditioner,
  Amenity.Accessible,
  Amenity.Grating,
  Amenity.IsFurnished,
  Amenity.SunBoiler,
  Amenity.Pool,
];

export const ADDITIONAL_RESIDENTIAL_AMENITIES = [
  Amenity.BuildingOnStilts,
];

export const EXTRA_RESIDENTIAL_RENT_AMENITIES = [
  Amenity.UnitPetsAllowed,
  Amenity.ForRoommates,
];

export const RESIDENTIAL_AMENITIES_COUNTS: AmenityCountType[] = [
  AmenityCountType.Parking,
  AmenityCountType.Bathroom,
  AmenityCountType.Toilet,
  AmenityCountType.AirDirection,
];

export const RESIDENTIAL_SIZED_AMENITIES: SizedAmenityType[] = [
  SizedAmenityType.Balcony,
  SizedAmenityType.Garden,
  SizedAmenityType.Warehouse,
];

export const AMENITIES_WITH_EXTRA_DATA_LIST: IAmenityWithExtraDataItem[] = [
  ...RESIDENTIAL_SIZED_AMENITIES.map((v: SizedAmenityType) => ({ type: AmenityWithExtraDataType.Sized, value: v })),
  ...RESIDENTIAL_AMENITIES_COUNTS.map((v: AmenityCountType) => ({ type: AmenityWithExtraDataType.Count, value: v })),
] as IAmenityWithExtraDataItem[];

export const AMENITIES_WITH_EXTRA_DATA_COLUMN: IAmenityWithExtraDataItem[] = [ {
  type: AmenityWithExtraDataType.Sized,
  value: SizedAmenityType.Balcony,
}, {
  type: AmenityWithExtraDataType.Sized,
  value: SizedAmenityType.Warehouse,
}, {
  type: AmenityWithExtraDataType.Count,
  value: AmenityCountType.Bathroom,
}, {
  type: AmenityWithExtraDataType.Count,
  value: AmenityCountType.AirDirection,
}, {
  type: AmenityWithExtraDataType.Sized,
  value: SizedAmenityType.Garden,
}, {
  type: AmenityWithExtraDataType.Count,
  value: AmenityCountType.Parking,
}, {
  type: AmenityWithExtraDataType.Count,
  value: AmenityCountType.Toilet,
} ];

export const COMMERCIAL_AMENITIES = [
  Amenity.Doorman,
  Amenity.Storage,
  Amenity.AirConditioner,
  Amenity.Elevator,
  Amenity.OuterSpace,
  Amenity.SecureRoom,
  Amenity.Mamak,
  Amenity.Miklat,
  Amenity.ParkingEmployee,
  Amenity.ParkingBikes,
  Amenity.ParkingVisitors,
  Amenity.Accessible,
  Amenity.Kitchenette,
  Amenity.FullTimeAccess,
  Amenity.Alarm,
  Amenity.ConferenceRoom,
  Amenity.SubDivisible,
  Amenity.Reception,
  Amenity.ColdRoom,
  Amenity.LoadingRamp,
  Amenity.HighCeiling,
  Amenity.ActiveBusiness,
];

export const DESCRIPTION_MAX_LENGTH = 500;

export const BULLETIN_TYPES: ListingPoiType[] = [ 'bulletin', 'commercialBulletin' ];

export const AVAILABILITY_OPTIONS: AvailabilityType[] = [
  AvailabilityType.Immediate,
  AvailabilityType.Flexible,
  AvailabilityType.Date,
];

export const DEAL_TYPES: DealType[] = [ DealType.Buy, DealType.Rent ];

export const resolutionPrefOptions = [
  ResolutionPreference.Accurate,
  ResolutionPreference.Street,
  ResolutionPreference.Neighborhood,
];
